/**
 * HubSpot tracking helpers shared across the apps.
 *
 * The HubSpot loader sets a persistent `hubspotutk` cookie on the apex domain
 * (e.g. `.nativeteams.com`) which binds the visitor to whatever email we last
 * called `_hsq.push(['identify', { email }])` with. On a shared device this
 * causes subsequent form submissions from a different user to be merged onto
 * the previous user's HubSpot contact (their email is added as a secondary
 * email instead of creating a new contact).
 */

interface HubSpotIdentifyTraits {
  email: string;
  [key: string]: string | number | boolean | null;
}

type HubSpotQueueCommand =
  | ['identify', HubSpotIdentifyTraits]
  | ['trackPageView']
  | ['setPath', string];

declare global {
  interface Window {
    _hsq?: HubSpotQueueCommand[];
  }
}

const HUBSPOT_COOKIE_NAMES = ['hubspotutk', '__hstc', '__hssc', '__hssrc'] as const;
const LAST_IDENTIFIED_EMAIL_KEY = 'nt:hs:last-identified-email';

function getDomainCandidates(hostname: string): string[] {
  if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return [];
  }

  const parts = hostname.split('.');
  const candidates: string[] = [];

  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    candidates.push(domain, `.${domain}`);
  }

  return candidates;
}

function deleteCookieEverywhere(name: string): void {
  if (typeof document === 'undefined') return;

  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const domains = getDomainCandidates(window.location.hostname);

  document.cookie = `${name}=; expires=${past}; path=/; SameSite=Lax`;

  for (const domain of domains) {
    document.cookie = `${name}=; expires=${past}; path=/; domain=${domain}; SameSite=Lax`;
  }
}

/**
 * Wipes HubSpot tracking cookies (`hubspotutk`, `__hstc`, `__hssc`, `__hssrc`)
 * on every parent domain so the browser starts as a fresh, anonymous HubSpot
 * visitor.
 *
 * Only call this when the identified user actually changes (handled inside
 * `identifyHubSpotUser`). Do NOT wire it into logout: deleting these cookies
 * during the logout/redirect flow triggered a "Something went wrong" error
 * screen (see the reverted attempt in commit bea36f863), and doing it on every
 * load churns `hubspotutk` and breaks contact-targeted pop-ups.
 *
 * Note: this intentionally does NOT push `revokeCookieConsent`, which would
 * disable tracking entirely. Consent is managed separately via the inline
 * consent script in each app layout.
 *
 * Safe to call from any client component / browser-side code. No-ops on the
 * server.
 */
export function resetHubSpotVisitor(): void {
  if (typeof window === 'undefined') return;

  for (const name of HUBSPOT_COOKIE_NAMES) {
    deleteCookieEverywhere(name);
  }

  clearLastIdentifiedEmail();
}

function readLastIdentifiedEmail(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(LAST_IDENTIFIED_EMAIL_KEY);
  } catch {
    return null;
  }
}

function setLastIdentifiedEmail(email: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_IDENTIFIED_EMAIL_KEY, email);
  } catch {
    // ignore
  }
}

function clearLastIdentifiedEmail(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LAST_IDENTIFIED_EMAIL_KEY);
  } catch {
    // ignore storage access errors (e.g. third-party cookie blocks)
  }
}

/**
 * Identifies the current user with HubSpot.
 *
 * IMPORTANT: this runs on every authenticated page load (via
 * `HubSpotIdentityBinding`), so it must NOT reset the visitor unconditionally.
 * Wiping `hubspotutk` on every load mints a new visitor token each time, which
 * stops HubSpot resolving the browser to a known contact — contact-targeted
 * pop-ups / CTAs then silently never appear.
 *
 * Instead we only reset when the identified email actually changes (a different
 * user on the same browser), and skip entirely when it's the same user. This
 * keeps `hubspotutk` stable across a session so pop-up targeting works, while
 * still protecting against cross-user contact merging — without touching the
 * logout flow (resetting there caused a "Something went wrong" error screen).
 *
 * Returns `true` when the identify call was queued, `false` when skipped
 * (already identified with the same email, no email provided, or SSR).
 */
export function identifyHubSpotUser(email: string | null | undefined): boolean {
  if (typeof window === 'undefined' || !email) return false;

  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;

  const previous = readLastIdentifiedEmail();

  if (previous && previous !== normalized) {
    // Different user on the same browser — clear the prior visitor first.
    resetHubSpotVisitor();
  } else if (previous === normalized) {
    // Same user already identified this session — nothing to do.
    return false;
  }

  window._hsq = window._hsq || [];
  window._hsq.push(['identify', { email: normalized }]);
  window._hsq.push(['trackPageView']);

  setLastIdentifiedEmail(normalized);
  return true;
}
